// components/secure/OnboardingTemplateEditor.js — Onboarding → Edit templates.
//
// The step lists behind "Start onboarding" (licensee, employee, exits) could only be
// changed by a seed script or SQL. The managing broker now edits them here: add a
// step, reword it, reorder, set the due days, required / blocking, and the rule it
// rests on. What the system does for a step (the form it issues, the automation, the
// document it waits for) is carried forward unchanged — shown, not edited, because a
// wrong form key would issue the wrong paperwork.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from './Modal';

const STEP_TYPES = ['manual', 'send_welcome_email', 'assign_email', 'verify_license', 'acknowledge_policy',
    'sign_ica', 'idfpr_sponsorship', 'shp_training', 'collect_document'];
const input = 'w-full border border-slate-300 rounded px-2 py-1 text-sm';

export default function OnboardingTemplateEditor({ open, onClose }) {
    const api = useApiClient();
    const [templates, setTemplates] = useState([]);
    const [tid, setTid] = useState('');
    const [steps, setSteps] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [error, setError] = useState('');
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async (keep) => {
        try {
            const t = await api.get('/api/onboarding/templates?include_steps=1');
            setTemplates(t || []);
            const id = keep || (t[0] && t[0].template_id) || '';
            setTid(id);
            setSteps((((t || []).find((x) => x.template_id === Number(id)) || {}).steps || []).map((x) => ({ ...x, _orig: x.title })));
            setDirty(false);
        } catch (e) { setError(e.message); }
    }, [api]);
    useEffect(() => { if (open) load(); }, [open, load]);

    const pick = (id) => {
        if (dirty && !window.confirm('Discard the unsaved changes to this template?')) return;
        setTid(Number(id));
        setSteps(((templates.find((x) => x.template_id === Number(id)) || {}).steps || []).map((x) => ({ ...x, _orig: x.title })));
        setDirty(false); setMsg(''); setError('');
    };
    const change = (i, k, v) => { setSteps((s) => s.map((x, j) => (j === i ? { ...x, [k]: v } : x))); setDirty(true); };
    const move = (i, d) => {
        const j = i + d;
        if (j < 0 || j >= steps.length) return;
        const s = [...steps]; [s[i], s[j]] = [s[j], s[i]]; setSteps(s); setDirty(true);
    };
    const remove = (i) => {
        if (!window.confirm(`Remove "${steps[i].title}" from this template? People already being onboarded keep it.`)) return;
        setSteps((s) => s.filter((_, j) => j !== i)); setDirty(true);
    };
    const add = () => { setSteps((s) => [...s, { title: '', description: '', step_type: 'manual', required: 1, is_blocking: 0, due_days: '', authority: '', _new: true }]); setDirty(true); };

    const save = async () => {
        if (!tid) return;
        setBusy(true); setError(''); setMsg('');
        try {
            const body = steps.map((s) => ({
                title: s.title, previous_title: s._orig || undefined, description: s.description || null, step_type: s.step_type,
                policy_doc_type: s.policy_doc_type || null, required: !!Number(s.required),
                is_blocking: !!Number(s.is_blocking), authority: s.authority || null,
                due_days: s.due_days === '' || s.due_days == null ? null : Number(s.due_days),
                // form_key / automation / document_type are omitted on purpose: the server
                // carries them forward from the step's previous title.
            }));
            const r = await api.put(`/api/onboarding/templates/${tid}/steps`, { steps: body });
            setMsg(r.message); await load(tid);
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    return (
        <Modal open={open} onClose={() => { if (!dirty || window.confirm('Close without saving?')) onClose(); }} size="lg" title="Onboarding templates"
            footer={<>
                <button onClick={add} disabled={!tid} className="px-3 py-1.5 text-sm border border-slate-300 rounded disabled:opacity-50">+ Add step</button>
                <button onClick={save} disabled={busy || !dirty || !tid} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded disabled:bg-blue-300">{busy ? 'Saving…' : 'Save template'}</button>
            </>}>
            {error && <p className="mb-2 text-sm text-red-600">{error}</p>}
            {msg && <p className="mb-2 text-sm text-green-700">{msg}</p>}
            {/* With no template there is nothing to save into: the editor edits templates, it
                does not create them, and saving used to PUT /templates//steps (404 "Not found."). */}
            {!templates.length && !error && <p className="mb-2 text-sm text-amber-800">No onboarding templates exist on this site yet, so there is nothing to edit. The administrator loads them with scripts/seed-process-templates.js.</p>}
            <div className="flex items-center gap-2 mb-3 text-sm">
                <span>Template</span>
                <select value={tid} onChange={(e) => pick(e.target.value)} className="border border-slate-300 rounded px-2 py-1">
                    {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name} ({t.audience})</option>)}
                </select>
                <span className="text-xs text-slate-500">Changes apply to onboardings started from now on.</span>
            </div>
            <ol className="space-y-3">
                {steps.map((s, i) => (
                    <li key={`${s.step_id || 'new'}-${i}`} className="border border-slate-200 rounded p-3">
                        <div className="flex items-start gap-2">
                            <span className="text-xs text-slate-400 w-6 pt-1.5">{i + 1}.</span>
                            <div className="flex-1 space-y-2">
                                <input className={input} placeholder="Step title" value={s.title || ''} onChange={(e) => change(i, 'title', e.target.value)} />
                                <textarea className={input} rows={2} placeholder="What to do" value={s.description || ''} onChange={(e) => change(i, 'description', e.target.value)} />
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                    <label>Type
                                        <select className={input} value={s.step_type || 'manual'} onChange={(e) => change(i, 'step_type', e.target.value)}>
                                            {STEP_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                                        </select></label>
                                    <label>Due after (days)
                                        <input className={input} type="number" min="0" value={s.due_days ?? ''} onChange={(e) => change(i, 'due_days', e.target.value)} /></label>
                                    <label className="flex items-center gap-1 mt-4"><input type="checkbox" checked={!!Number(s.required)} onChange={(e) => change(i, 'required', e.target.checked ? 1 : 0)} /> Required</label>
                                    <label className="flex items-center gap-1 mt-4"><input type="checkbox" checked={!!Number(s.is_blocking)} onChange={(e) => change(i, 'is_blocking', e.target.checked ? 1 : 0)} /> Blocks working</label>
                                </div>
                                {s.step_type === 'acknowledge_policy' && (
                                    <label className="block text-xs">Policy document type
                                        <input className={input} value={s.policy_doc_type || ''} placeholder="e.g. office_policy_manual" onChange={(e) => change(i, 'policy_doc_type', e.target.value)} /></label>
                                )}
                                <label className="block text-xs">Rule it rests on (shown to the person)
                                    <input className={input} value={s.authority || ''} placeholder="e.g. 225 ILCS 454/5-40" onChange={(e) => change(i, 'authority', e.target.value)} /></label>
                                {(s.form_key || s.automation || s.document_type) && (
                                    <p className="text-xs text-slate-500">Automatic: {[s.form_key && `issues form ${s.form_key}`, s.automation && `does ${s.automation.replace(/_/g, ' ')}`, s.document_type && `waits for upload: ${s.document_type}`].filter(Boolean).join(' · ')}.</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1 text-xs">
                                <button onClick={() => move(i, -1)} disabled={i === 0} className="px-2 py-0.5 border rounded disabled:opacity-30">↑</button>
                                <button onClick={() => move(i, 1)} disabled={i === steps.length - 1} className="px-2 py-0.5 border rounded disabled:opacity-30">↓</button>
                                <button onClick={() => remove(i)} className="px-2 py-0.5 border rounded text-red-600">✕</button>
                            </div>
                        </div>
                    </li>
                ))}
            </ol>
        </Modal>
    );
}
